import React from 'react';
import { ShieldCheck, Shield, Plane, Leaf, Server, Lock, Brain, Heart, CheckCircle, ArrowRight, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const IsoServices = () => {
  const navigate = useNavigate();

  const isoStandards = [
    {
      icon: ShieldCheck,
      code: 'ISO 9001',
      title: 'Quality Management Systems',
      description: 'A universal blueprint for organizations aiming to deliver consistent, high-quality products and services through optimized processes.',
      benefits: [
        'Enhanced customer satisfaction',
        'Reduced product failures',
        'Efficient workflows',
        'Stronger competitive advantage'
      ],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Server,
      code: 'ISO/IEC 20000-1',
      title: 'IT Service Management',
      description: 'International benchmark for IT service delivery, transforming reactive support into proactive, systematic service management.',
      benefits: [
        'Configuration management',
        'Reduced system downtime',
        'Enhanced cybersecurity',
        'Business-aligned IT services'
      ],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Lock,
      code: 'ISO/IEC 27001',
      title: 'Information Security Management',
      description: 'Gold standard for protecting information assets, covering digital data, physical documents, and organizational knowledge.',
      benefits: [
        'Confidentiality & access control',
        'Data integrity assurance',
        'System availability',
        'GDPR compliance integration'
      ],
      gradient: 'from-red-500 to-orange-500'
    },
    {
      icon: Brain,
      code: 'ISO/IEC 42001',
      title: 'Artificial Intelligence Management',
      description: 'Comprehensive framework for ethical, transparent, and compliant AI development, deployment, and maintenance.',
      benefits: [
        'Ethical AI governance',
        'Risk mitigation strategies',
        'Regulatory compliance',
        'Future-proof AI policies'
      ],
      gradient: 'from-violet-500 to-fuchsia-500'
    },
    {
      icon: Heart,
      code: 'ISO 45001',
      title: 'Occupational Health & Safety',
      description: 'Dynamic, process-driven system for preventing workplace injuries and promoting a culture of safety using the PDCA methodology.',
      benefits: [
        'Active worker participation',
        'Proactive hazard identification',
        'Injury prevention',
        'Reduced corporate liability'
      ],
      gradient: 'from-rose-500 to-red-500'
    },
    {
      icon: Leaf,
      code: 'ISO 14001',
      title: 'Environmental Management Systems',
      description: 'Framework for measuring and reducing environmental impact, emphasizing climate resilience, biodiversity, and product lifecycle management.',
      benefits: [
        'Waste & energy optimization',
        'Carbon emission reduction',
        'Regulatory compliance',
        'Cost savings through efficiency'
      ],
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Plane,
      code: 'AS/EN 9100',
      title: 'Aerospace Quality Management',
      description: 'Specialized standards for aviation, space, and defense sectors with rigorous requirements for precision and safety.',
      benefits: [
        'AS9100: Design & Manufacturing',
        'AS9110: Repair & Maintenance',
        'AS9120: Distribution & Warehousing',
        'Global safety compliance'
      ],
      gradient: 'from-indigo-500 to-purple-500',
      layoutClass: 'lg:col-start-2'
    }
  ];

  const handleScroll = (href) => {
    if (href.startsWith('#')) {
      navigate('/' + href);
    }
  };

  return (
    <section id="iso-services" className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-b from-slate-50 via-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider">
              <Award className="w-4 h-4" />
              International Organization for Standardization
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            ISO <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-600"> Services</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Achieve excellence through globally recognized accredited ISO certifications. From quality management to cybersecurity, environmental sustainability to AI governance—we guide you through comprehensive certification journeys.
          </p>
        </div>

        {/* ISO Standards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {isoStandards.map((standard, index) => {
            const IconComponent = standard.icon;
            return (
              <div 
                key={index} 
                className={`bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-2xl transition-all duration-300 group ${standard.layoutClass || ''}`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${standard.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <div className="mb-4">
                  <span className="inline-block bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold mb-3">
                    {standard.code}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {standard.title}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {standard.description}
                </p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Key Benefits:</p>
                  {standard.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Choose ISO Certification Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 rounded-2xl p-8 sm:p-12 shadow-lg border-2 border-blue-200/50">
            <h3 className="text-3xl font-bold text-slate-900 mb-6 text-center">Why ISO Certification Matters</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Global Recognition</h4>
                <p className="text-slate-600 text-sm">
                  ISO certifications are recognized worldwide, opening doors to international markets and partnerships.
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Competitive Advantage</h4>
                <p className="text-slate-600 text-sm">
                  Demonstrate your commitment to quality, security, and excellence to win more contracts and customers.
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Operational Excellence</h4>
                <p className="text-slate-600 text-sm">
                  Improve processes, reduce costs, and enhance efficiency through systematic management frameworks.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 rounded-3xl p-10 sm:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -ml-20 -mb-20"></div>
          <div className="relative z-10">
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Get ISO Certified?</h3>
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              Our expert consultants will guide you through every step of the certification process, ensuring compliance and excellence.
            </p>
            <Button 
              onClick={() => handleScroll('#contact')} 
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-blue-50 text-blue-700 font-bold px-8 py-4 rounded-xl hover:shadow-xl transition-all duration-300"
            >
              Start Your Certification Journey
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IsoServices;
